
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {LibraryLayout} from '../../components/Library'

export const metadata = {
  "title": "Alternative Art School Fair Catalog",
  "date": "2021-04-28",
  "author": "Brendan",
  "toc": true,
  "tags": ["bookshelf"],
  "description": "Notes on alternative learning institutions from Pioneer Works' Alternative Art School Fair catalog.",
  "topic": "https://forum.hyperlink.academy/t/book-notes-alternative-art-school-fair/2778"
};

const layoutProps = {
  metadata
};
const MDXLayout = ({children}) => <LibraryLayout {...metadata}>{children}</LibraryLayout>
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`In 2016, Pioneer Works hosted the `}<a parentName="p" {...{
        "href": "https://pioneerworks.org/programs/alternative-art-school-fair/"
      }}>{`Alternative Art School Fair`}</a>{`, a fantastic weekend-long "introduction to alternative art schools from around the US and the world."`}</p>
    <p>{`This was one part of a whole lineup of great pedagogical programming organized by `}<a parentName="p" {...{
        "href": "https://www.catherinedespont.com/alternative-art-school-fair"
      }}>{`Catherine Despont`}</a>{`, who served as Pioneer Works' Director of Education for a number of years. (I also attended and enjoyed the Summit on Pedagogy and a few of the monthly Teaching Roundtable meetups Catherine hosted.)`}</p>
    <p>{`Accompanying the fair, Pioneer Works published a catalog, by the same name, featuring descriptions of the many institutions that participated. I recently read through my copy again, and highlighted a number of schools and passages that stood out as interesting.`}</p>
    <p>{`From the introductory description of the Alternative Art School Fair:`}</p>
    <blockquote>
      <p parentName="blockquote">{`Art education is a reflection of social and cultural evolution; it engages with structures of meaning-making and considers different frameworks for experience. The impetus to create an alternative art school is rooted not only in a desire to create “better” art, but to create the conditions for greater freedom of expression. Often run as free, artist-run initiatives, the values and visions of alternative art schools vary widely in methodology, mission and governance. But even when they are relatively small in scale they provide vital models of cultural critique and experimentation.`}</p>
    </blockquote>
    <p>{`Below, find a sampling of the several dozen alternative schools, publishers, experimental spaces, and other learning institutions, and quotes from the pages of the AASF catalog.`}</p>
    <p>{`Hyperlink is greatly inspired by creative and alternative pedagogy in general, and many of these institutions in particular.`}</p>
    <p>{`We think you should know about them — browse their websites, read their work, and look to them as examples of how learning spaces can be more creative, exploratory, and transformative.`}</p>
    <hr></hr>
    <p><em parentName="p">{`NB 1: the AASF catalog doesn't appear to be in print / available for purchase online. I did, however, find a `}<a parentName="em" {...{
          "href": "https://biennaledeparis.org/doc/AASF_2016_CAT_WEB.pdf"
        }}>{`PDF available online here`}</a>{`.`}</em></p>
    <p><em parentName="p">{`NB 2: I linked as many websites as I could find; some are no longer online.`}</em></p>
    <hr></hr>
    <h3 {...{
      "id": "altmfa"
    }}><a parentName="h3" {...{
        "href": "https://altmfa.blogspot.com/"
      }}>{`AltMFA`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`AltMFA incorporates the most desirable elements of an MFA course; space to work, collaborators to argue with, a social sphere to move within…the course does not have a finite end…`}</p>
    </blockquote>
    <h3 {...{
      "id": "anhoek-school"
    }}><a parentName="h3" {...{
        "href": "https://anhoek.blogspot.com/"
      }}>{`Anhoek School`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Anhoek School eschews classes; we pervert and perform the form of the standardized test.`}</p>
    </blockquote>
    <h3 {...{
      "id": "antiuniversity-now"
    }}><a parentName="h3" {...{
        "href": "http://antiuniversity.org/"
      }}>{`Antiuniversity Now`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`The Antiuniversity Now project was set up in 2015 to challenge academic and class hierarchy through an open invitation to teach and learn any subject, in any form, anywhere.`}</p>
    </blockquote>
    <h3 {...{
      "id": "beta-local"
    }}><a parentName="h3" {...{
        "href": "https://betalocal.org/"
      }}>{`Beta-Local`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Our work is focused on generating new models for art making that respond to our social and material realities, which are: a debt-ridden state, a collapsed consumer society model, a colonial economic structure and government, and rampant deskilling.`}</p>
    </blockquote>
    <h3 {...{
      "id": "black-mountain-school"
    }}><a parentName="h3" {...{
        "href": "https://bms.kusd.org/"
      }}>{`Black Mountain School`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`BMS is a socially integrated experience, with intimate classes, communal living, and emphasis on shared labor. Educators create the core content, students determine their level of engagement, and the community is sustained through collective participation.`}</p>
    </blockquote>
    <h3 {...{
      "id": "brooklyn-institute-for-social-research"
    }}><a parentName="h3" {...{
        "href": "https://thebrooklyninstitute.com/"
      }}>{`Brooklyn Institute for Social Research`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Our mission is to make graduate level education available to the greatest number all the while supporting young scholars and their work.`}</p>
    </blockquote>
    <h3 {...{
      "id": "école-de-hogbon"
    }}><a parentName="h3" {...{
        "href": "https://ecoledehogbonu.com/"
      }}>{`école de Hogbon`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`…art that is whispered silently in life like a heart beat…a non-school without any other finality than to provide time for exploration, idleness and wandering; a melting pot for transmitting and sharing knowledge as well as uncertainties, disquiet, doubts.`}</p>
    </blockquote>
    <h3 {...{
      "id": "enrol-yourself"
    }}><a parentName="h3" {...{
        "href": "https://www.enrolyourself.com/"
      }}>{`Enrol Yourself`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Set out on a learning marathon. Design your route. Crowd-source the support you need. Stretch your creative capacities.`}</p>
    </blockquote>
    <h3 {...{
      "id": "escuela-parasitio"
    }}><a parentName="h3" {...{
        "href": "https://parasiteschool.org/about"
      }}>{`Escuela Parasitio`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Escuela Parasitio infiltrates the university in a parasitical way and uses its resources, people and infrastructure to embrace & serve immigrants and artists-nomads excluded by official universities in the USA and Europe. (101)`}</p>
    </blockquote>
    <h3 {...{
      "id": "grizedale-arts"
    }}><a parentName="h3" {...{
        "href": "https://www.grizedale.org/"
      }}>{`Grizedale Arts`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Everything at Grizedale is seen as a project, and this includes the farmhouse, the garden and the washing up…skill and labor are exchanged for a learning experience and the potential for involvement in the ever-expanding Grizedale collection of collectives — groups with interests and skills from administrative to practical, cooking to art theory.`}</p>
    </blockquote>
    <h3 {...{
      "id": "ox-bow-school-of-art"
    }}><a parentName="h3" {...{
        "href": "https://www.ox-bow.org/"
      }}>{`Ox-Bow School of Art`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Ox-Bow's approach to learning is one that seeks to combine traditions of craft and technical expertise with conceptual, experimental, and playful approaches to art making. Courses are both rigorous and responsive, valuing non-traditional and innovative educational models.`}</p>
    </blockquote>
    <h3 {...{
      "id": "nomad9"
    }}><a parentName="h3" {...{
        "href": "https://www.nomadmfa.org/"
      }}>{`Nomad/9`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`The 26-month, accredited program engages sites in the Americas, using a living classroom approach to hands-on learning. During each residency, students engage in a reciprocal relationship with place, learning from local practitioners and contributing to regional initiatives.`}</p>
    </blockquote>
    <h3 {...{
      "id": "ricean-school-of-dance"
    }}><a parentName="h3" {...{
        "href": "http://www.riceonhydra.org/"
      }}>{`Ricean School of Dance`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`RSOD engages no teachers or mentors but invites exchange that comes from the sharing of knowledge.`}</p>
    </blockquote>
    <h3 {...{
      "id": "school-of-the-future"
    }}><a parentName="h3" {...{
        "href": "https://www.sof.edu/"
      }}>{`School of the Future`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`In 2010 it grew into an outdoor intergenerational free school in North Brooklyn. … Since then it has become an archive of lessons learned and a network of radical educators…`}</p>
    </blockquote>
    <h3 {...{
      "id": "school-for-poetic-computation"
    }}><a parentName="h3" {...{
        "href": "https://sfpc.io/"
      }}>{`School for Poetic Computation`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`It's a hybrid of a school, residency, and research group.`}</p>
    </blockquote>
    <h3 {...{
      "id": "shiftwork"
    }}><a parentName="h3" {...{
        "href": "http://www.shift-work.org.uk/"
      }}>{`Shift/Work`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Shift/Work has established a collective ontology for artistic practice through a paragogy in which participants design, playtest and reflect upon their learning processes.`}</p>
    </blockquote>
    <h3 {...{
      "id": "southland-institute"
    }}><a parentName="h3" {...{
        "href": "http://www.southland.institute/"
      }}>{`Southland Institute`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Southland Institute is an unaccredited two-year postgraduate program and workshop built around a central curricular helix consisting of typography and critical art making, incorporating tools, processes, histories, and discourses of each of these strands. It is also an inquiry into the process, potential, and complications of education itself.`}</p>
    </blockquote>
    <h3 {...{
      "id": "the-black-school"
    }}><a parentName="h3" {...{
        "href": "https://theblack.school/"
      }}>{`The Black School`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Students in the program will be required to participate in a continuum of art projects: asking them to apply the art making techniques and critical thinking learned in the program to address issues in their community.`}</p>
    </blockquote>
    <h3 {...{
      "id": "the-public-school"
    }}><a parentName="h3" {...{
        "href": "https://thepublicschool.org/"
      }}>{`The Public School`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`It is a framework that supports autodidactic activities, operating under the assumption that everything is in everything. … We will turn these into an open source format, like a malleable blueprint, for new classes. These will then be available for everyone on Syllabus-hub, a website for a syllabus sharing community.`}</p>
    </blockquote>
    <h3 {...{
      "id": "the-school-of-making-thinking"
    }}><a parentName="h3" {...{
        "href": "http://www.theschoolofmakingthinking.com/"
      }}>{`The School of Making Thinking`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Our program asks: How does art deepen thought and provoke questioning? How is thinking enacted through creative mediums? And how can an environment be structured (or resist structuring) in such a way that these questions can not only be asked, but be lived as well? … In admitting to never fully knowing how things will happen until they happen, we believe that SMT's fluidity and non-sedimented nature can allow for exciting unfolding.`}</p>
    </blockquote>
    <h3 {...{
      "id": "thinker-space"
    }}>{`Thinker Space`}</h3>
    <blockquote>
      <p parentName="blockquote">{`Each participant (including the facilitators) propose one unanswerable question that they would like to explore for the course of the semester.`}</p>
    </blockquote>
    <h3 {...{
      "id": "transart-institute"
    }}><a parentName="h3" {...{
        "href": "https://www.transartinstitute.org/"
      }}>{`Transart Institute`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Adopting a respiratory model of meeting and dispersing, Transart positions itself in the gaps where art practice, knowledge production and research processes operate; in the tensions between, across, through, and beyond recognized paradigms.`}</p>
    </blockquote>
    <h3 {...{
      "id": "uncertainty-school"
    }}>{`Uncertainty School`}</h3>
    <blockquote>
      <p parentName="blockquote">{`Uncertainty School is a school to explore potential that cannot be described in a language of the world of certainty. Uncertainty School is inclusive for people who have distinct senses and ways of communicating, moving and thinking.`}</p>
    </blockquote>
    <h3 {...{
      "id": "union-of-initiatives-for-educational-assembly"
    }}><a parentName="h3" {...{
        "href": "http://www.yuoiea.com/"
      }}>{`Union of Initiatives for Educational Assembly`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`The UOIEA invokes the frameworks and aesthetics of early child pedagogy as model and method for artistic engagement.`}</p>
    </blockquote>
    <h3 {...{
      "id": "common-field"
    }}><a parentName="h3" {...{
        "href": "https://www.commonfield.org/"
      }}>{`Common Field`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`Common Field is a network of contemporary experimental artist organizations and organizers…a platform for learning and exchange.`}</p>
    </blockquote>
    <h3 {...{
      "id": "inventory-press"
    }}><a parentName="h3" {...{
        "href": "http://www.inventorypress.com/"
      }}>{`Inventory Press`}</a></h3>
    <blockquote>
      <p parentName="blockquote">{`[Published `}<a parentName="p" {...{
          "href": "http://www.inventorypress.com/product/blueprint-for-counter-education"
        }}>{`Blueprint for Counter Education`}</a>{`, an] exploratory box set designed to encourage self-directed learning.`}</p>
    </blockquote>
    <h3 {...{
      "id": "open-source-and-space-administration-institute-for-alternative-research"
    }}>{`Open Source and Space Administration Institute for Alternative Research`}</h3>
    <blockquote>
      <p parentName="blockquote">{`OSSAI experiments with open source approach to education, research, information sharing and alternative space creation.`}</p>
    </blockquote>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;